import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PageLayout, PageHeader } from '../../components'
import { StatusChip, LoadingSpinner, ToastNotification } from '../../components/atoms'
import { InvoiceHistorySection } from '../user/Billing'
import { PlanManagementContent } from './AdminBillingPlanManagement'
import ContactsPanel from './ContactsPanel'
import useBillingData from '../../hooks/useBillingData'
import { useToast } from '../../hooks'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import styles from './AdminBillingClientView.module.scss'

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'chart-pie' },
  { id: 'plans', label: 'Plan Management', icon: 'file-invoice-dollar' },
  { id: 'contacts', label: 'Contacts', icon: 'users' },
  { id: 'invoices', label: 'Invoices & Usage', icon: 'file-invoice-dollar' },
]

const COUNTRY_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'CA', label: 'Canada (CA)' },
  { value: 'US', label: 'United States (US)' },
]

const PREFERRED_METHOD_LABELS = {
  credit_card: 'Credit Card',
  pad: 'PAD',
  ach: 'ACH',
}

const OverviewTab = ({ customer, onCountryChange, onBillingActiveChange, activePlan }) => {
  const planMinutes = activePlan?.campaign_plan_minutes || 0
  const currentMinutes = parseFloat(customer.current_month_minutes) || 0
  const usagePercent =
    planMinutes > 0 ? Math.min(100, Math.round((currentMinutes / planMinutes) * 100)) : 0
  const isOver = currentMinutes > planMinutes && planMinutes > 0
  const pm = customer.default_payment_method_summary_json
  const [bankDetails, setBankDetails] = useState(null)
  const [contacts, setContacts] = useState([])

  useEffect(() => {
    if (!customer.id) return
    let cancelled = false
    const fetchContacts = async () => {
      try {
        const res = await authenticatedFetchJson(apiEndpoints.billingCustomerContacts(customer.id))
        if (!cancelled) setContacts(res?.data || [])
      } catch (_err) {
        /* silent */
      }
    }
    fetchContacts()
    return () => {
      cancelled = true
    }
  }, [customer.id])

  useEffect(() => {
    const method = customer.preferred_payment_method
    if (!customer.id || (method !== 'pad' && method !== 'ach')) return
    let cancelled = false
    const fetchBank = async () => {
      try {
        const res = await authenticatedFetchJson(
          apiEndpoints.billingCustomerBankDetails(customer.id),
        )
        if (!cancelled) setBankDetails(res?.data || null)
      } catch (err) {
        logger.error('Failed to fetch bank details for admin view:', err)
        if (!cancelled) setBankDetails(null)
      }
    }
    fetchBank()
    return () => {
      cancelled = true
    }
  }, [customer.id, customer.preferred_payment_method])

  return (
    <div className={styles.overviewGrid}>
      <div className={styles.statusCard}>
        <h4 className={styles.statusCardTitle}>Billing Profile</h4>
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>Stripe Linked</span>
          <StatusChip
            label={customer.stripe_customer_id ? 'Yes' : 'No'}
            color={customer.stripe_customer_id ? 'success' : 'neutral'}
            size="small"
          />
        </div>
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>Currency</span>
          <span className={styles.statusValue}>{customer.billing_currency_default}</span>
        </div>
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>Country</span>
          <select
            className={styles.countrySelect}
            value={customer.country_code || ''}
            onChange={(e) => onCountryChange(e.target.value || null)}
            data-testid="country-select"
          >
            {COUNTRY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {customer.country_code === 'CA' && (
          <div className={styles.taxHint}>13% HST will be applied to invoices</div>
        )}
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>Campaign Billing</span>
          <label
            className={styles.toggleSwitch}
            title={
              customer.campaign_billing_status === 'active'
                ? 'Active — included in the monthly invoice cron'
                : 'Disabled — the cron will skip this campaign'
            }
          >
            <input
              type="checkbox"
              checked={customer.campaign_billing_status === 'active'}
              disabled={!customer.campaign_id}
              onChange={(e) => onBillingActiveChange(e.target.checked)}
              data-testid="campaign-billing-toggle"
            />
            <span className={styles.toggleSlider} />
            <span className={styles.toggleLabel}>
              {customer.campaign_billing_status === 'active' ? 'Active' : 'Disabled'}
            </span>
          </label>
        </div>
        {customer.campaign_billing_status !== 'active' && customer.campaign_id && (
          <div className={styles.warnHint}>
            Cron will skip this campaign until activated. Adding a same-day plan also activates
            automatically.
          </div>
        )}
      </div>

      <div className={styles.statusCard}>
        <h4 className={styles.statusCardTitle}>Current Usage</h4>
        <div className={styles.usageNumbers}>
          <span className={styles.usageBig}>{Math.round(currentMinutes)}</span>
          {planMinutes > 0 ? (
            <span className={styles.usageOf}> / {planMinutes} min</span>
          ) : (
            <span className={styles.usageOf}> minutes this month</span>
          )}
        </div>
        {planMinutes > 0 && (
          <>
            <div className={styles.usageBarTrack}>
              <div
                className={`${styles.usageBarFill} ${isOver ? styles.usageBarOver : ''}`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
            <div className={styles.usagePercent}>
              {isOver
                ? `${Math.round((currentMinutes / planMinutes) * 100)}% — over limit by ${Math.round(currentMinutes - planMinutes)} min`
                : `${usagePercent}% used this month`}
            </div>
          </>
        )}
        {planMinutes === 0 && (
          <p className={styles.emptyHint}>Set plan allocation in Plan Management to track usage.</p>
        )}
      </div>

      <div className={styles.statusCard}>
        <h4 className={styles.statusCardTitle}>Payment Method</h4>
        <div className={styles.statusRow}>
          <span className={styles.statusLabel}>Preferred</span>
          <StatusChip
            label={PREFERRED_METHOD_LABELS[customer.preferred_payment_method] || 'Credit Card'}
            color={customer.preferred_payment_method === 'credit_card' ? 'primary' : 'info'}
            size="small"
          />
        </div>
        {pm && pm.last4 && (
          <div className={styles.pmItem}>
            <span className={styles.pmIcon}>
              <FontAwesomeIcon icon="credit-card" />
            </span>
            <div>
              <div className={styles.pmDetail}>
                {pm.brand?.toUpperCase() || 'Card'} ••••{pm.last4}
              </div>
              <div className={styles.pmSub}>
                Exp {pm.exp_month}/{pm.exp_year}
              </div>
            </div>
          </div>
        )}
        {pm && pm.type === 'link' && (
          <div className={styles.pmItem}>
            <span className={styles.pmIcon}>
              <FontAwesomeIcon icon="link" />
            </span>
            <div>
              <div className={styles.pmDetail}>Link</div>
              <div className={styles.pmSub}>{pm.email}</div>
            </div>
          </div>
        )}
        {bankDetails && (
          <div className={styles.pmItem} data-testid="admin-bank-details">
            <span className={styles.pmIcon}>
              <FontAwesomeIcon icon="university" />
            </span>
            <div>
              <div className={styles.pmDetail}>
                {(bankDetails.payment_type || '').toUpperCase()} ••••
                {bankDetails.account_number_last4}
              </div>
              <div className={styles.pmSub}>
                {bankDetails.institution_name || bankDetails.bank_name || ''}
                {bankDetails.account_holder_name ? ` · ${bankDetails.account_holder_name}` : ''}
              </div>
            </div>
          </div>
        )}
        {!pm &&
          !customer.payment_method_present &&
          !bankDetails &&
          (customer.preferred_payment_method === 'pad' ||
          customer.preferred_payment_method === 'ach' ? (
            <p className={styles.emptyHint}>Bank details not yet submitted by client.</p>
          ) : customer.stripe_customer_id ? (
            <p className={styles.emptyHint}>No payment method on file.</p>
          ) : (
            <p className={styles.emptyHint}>Stripe not linked — no payment method available.</p>
          ))}
      </div>

      <div className={styles.statusCard}>
        <h4 className={styles.statusCardTitle}>Contacts</h4>
        {contacts.length > 0 ? (
          <div className={styles.contactList}>
            {contacts.map((c) => (
              <div
                key={c.id}
                className={`${styles.contactItem} ${c.is_primary ? styles.contactPrimary : ''}`}
              >
                <div className={styles.contactLeft}>
                  <div className={styles.contactAvatar}>
                    <FontAwesomeIcon icon="user" />
                  </div>
                  <div>
                    <div className={styles.pmDetail}>
                      {c.name && c.name !== c.email ? c.name : c.email}
                    </div>
                    {c.name && c.name !== c.email && <div className={styles.pmSub}>{c.email}</div>}
                  </div>
                </div>
                {c.is_primary ? (
                  <StatusChip label="Primary" color="success" size="small" />
                ) : (
                  <button
                    type="button"
                    className={styles.setPrimaryBtn}
                    onClick={async () => {
                      try {
                        await authenticatedFetchJson(
                          apiEndpoints.billingCustomerContact(customer.id, c.id),
                          {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ is_primary: true }),
                          },
                        )
                        // Refetch contacts
                        const res = await authenticatedFetchJson(
                          apiEndpoints.billingCustomerContacts(customer.id),
                        )
                        setContacts(res?.data || [])
                      } catch (_err) {
                        /* silent */
                      }
                    }}
                  >
                    Set primary
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.emptyHint}>No billing contacts.</p>
        )}
      </div>
    </div>
  )
}

OverviewTab.propTypes = {
  customer: PropTypes.object,
  onCountryChange: PropTypes.func,
  onBillingActiveChange: PropTypes.func,
  activePlan: PropTypes.object,
}

const STRIPE_STATUS_COLORS = {
  paid: 'success',
  open: 'warning',
  void: 'neutral',
  uncollectible: 'error',
  draft: 'neutral',
}

const formatStripeAmount = (amount, currency = 'usd') => {
  if (amount == null) return '—'
  return new window.Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100)
}

const formatStripeDate = (timestamp) => {
  if (!timestamp) return '—'
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const StripeInvoiceHistorySection = ({ billingCustomerId }) => {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!billingCustomerId) return
    const fetchStripeInvoices = async () => {
      try {
        const res = await authenticatedFetchJson(
          apiEndpoints.billingCustomerStripeInvoices(billingCustomerId),
        )
        setInvoices(res?.data || [])
      } catch (err) {
        logger.error('Failed to load Stripe invoices:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchStripeInvoices()
  }, [billingCustomerId])

  if (loading) return <p className={styles.stripeInvoiceEmpty}>Loading Stripe invoices...</p>
  if (invoices.length === 0) return null

  return (
    <div className={styles.stripeInvoiceSection} data-testid="stripe-invoice-section">
      <h3 className={styles.stripeInvoiceSectionTitle}>Stripe Invoice History</h3>
      <p className={styles.stripeInvoiceSubtitle}>
        Historical invoices from Stripe (before dashboard billing)
      </p>
      <div className={styles.stripeInvoiceTableWrapper}>
        <table className={styles.stripeInvoiceTable} data-testid="stripe-invoice-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Status</th>
              <th>Amount Due</th>
              <th>Amount Paid</th>
              <th>Date</th>
              <th>Due Date</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td>
                  {inv.hosted_invoice_url ? (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                      {inv.number || inv.id}
                    </a>
                  ) : (
                    inv.number || inv.id
                  )}
                </td>
                <td>
                  <StatusChip
                    label={inv.status}
                    color={STRIPE_STATUS_COLORS[inv.status] || 'neutral'}
                    size="small"
                  />
                </td>
                <td>{formatStripeAmount(inv.amount_due, inv.currency)}</td>
                <td>{formatStripeAmount(inv.amount_paid, inv.currency)}</td>
                <td>{formatStripeDate(inv.created)}</td>
                <td>{formatStripeDate(inv.due_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

StripeInvoiceHistorySection.propTypes = {
  billingCustomerId: PropTypes.number,
}

const AdminBillingClientView = ({ user: _user }) => {
  const { clientAccountId: rawId } = useParams()
  const clientAccountId = parseInt(rawId, 10)
  const navigate = useNavigate()
  const { customer, loading, error, refetch } = useBillingData(
    isNaN(clientAccountId) ? null : clientAccountId,
    null,
  )
  const { toast, showToast, closeToast } = useToast()
  const [activeTab, setActiveTab] = useState('overview')
  const [activePlan, setActivePlan] = useState(null)
  // Fetch active billing plan — refetch when switching back to overview tab
  useEffect(() => {
    if (!customer?.campaign_id || activeTab !== 'overview') return
    let cancelled = false
    const fetchPlan = async () => {
      try {
        const res = await authenticatedFetchJson(
          `${apiEndpoints.billingPlans}?campaign_id=${customer.campaign_id}`,
        )
        const plans = res?.data || []
        if (!cancelled) setActivePlan(plans.find((p) => p.is_active) || null)
      } catch (_err) {
        /* silent */
      }
    }
    fetchPlan()
    return () => {
      cancelled = true
    }
  }, [customer?.campaign_id, activeTab])

  /* eslint-disable react-hooks/preserve-manual-memoization */
  const handleCountryChange = useCallback(
    async (countryCode) => {
      if (!customer?.id) return
      try {
        await authenticatedFetchJson(`${apiEndpoints.billingCustomers}/${customer.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country_code: countryCode }),
        })
        showToast(
          countryCode === 'CA' ? 'Country set to Canada — 13% HST will apply' : 'Country updated',
          'success',
        )
        refetch()
      } catch (err) {
        logger.error('Country update failed:', err)
        // Stripe pins customer.currency on first charge; the backend refuses
        // mismatched country switches with code='region_locked'. Surface the
        // server's explanatory message instead of a generic failure toast.
        if (err.body && err.body.code === 'region_locked') {
          showToast(err.message, 'error')
        } else {
          showToast('Failed to update country', 'error')
        }
      }
    },
    [customer?.id, showToast, refetch],
  )
  /* eslint-enable react-hooks/preserve-manual-memoization */

  /* eslint-disable react-hooks/preserve-manual-memoization */
  const handleBillingActiveChange = useCallback(
    async (active) => {
      if (!customer?.campaign_id) return
      try {
        await authenticatedFetchJson(
          apiEndpoints.billingCampaignToggleBilling(customer.campaign_id),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ active }),
          },
        )
        showToast(
          active
            ? 'Campaign billing activated — included in the next monthly cron run'
            : 'Campaign billing disabled — cron will skip this campaign',
          'success',
        )
        refetch()
      } catch (err) {
        logger.error('Campaign billing toggle failed:', err)
        showToast(err.message || 'Failed to update campaign billing', 'error')
      }
    },
    [customer?.campaign_id, showToast, refetch],
  )
  /* eslint-enable react-hooks/preserve-manual-memoization */

  // Deep-link support: allow ?tab=contacts etc.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (tab && TABS.some((t) => t.id === tab)) setActiveTab(tab)
  }, [])

  if (loading) {
    return (
      <PageLayout>
        <PageHeader title="Client Billing" subtitle="Loading..." />
        <LoadingSpinner message="Loading billing data..." />
      </PageLayout>
    )
  }

  if (error || !customer) {
    return (
      <PageLayout>
        <PageHeader title="Client Billing" subtitle="Error" />
        <button className={styles.backButton} onClick={() => navigate('/billing/admin')}>
          <FontAwesomeIcon icon="chevron-left" /> Back to Billing Profiles
        </button>
        <div className={styles.errorState}>
          <p>{error || 'Billing customer not found'}</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <PageHeader title={customer.account_name || 'Client Billing'} subtitle="Admin billing view" />

      <div className={styles.topBar}>
        <button
          className={styles.backButton}
          onClick={() => navigate('/billing/admin')}
          data-testid="back-btn"
        >
          <FontAwesomeIcon icon="chevron-left" /> Back
        </button>
      </div>

      <div className={styles.tabBar} role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
          >
            <FontAwesomeIcon icon={tab.icon} className={styles.tabIcon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'overview' && (
          <OverviewTab
            customer={customer}
            onCountryChange={handleCountryChange}
            onBillingActiveChange={handleBillingActiveChange}
            activePlan={activePlan}
          />
        )}

        {activeTab === 'plans' &&
          (customer.campaign_id ? (
            <PlanManagementContent campaignId={String(customer.campaign_id)} />
          ) : (
            <div className={styles.emptyState}>
              <p>No campaign linked to this billing customer yet.</p>
            </div>
          ))}

        {activeTab === 'contacts' && (
          <ContactsPanel customer={customer} onToast={showToast} onChanged={refetch} />
        )}

        {activeTab === 'invoices' && (
          <>
            <InvoiceHistorySection billingCustomerId={customer.id} />
            {customer.stripe_customer_id && (
              <StripeInvoiceHistorySection billingCustomerId={customer.id} />
            )}
          </>
        )}
      </div>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </PageLayout>
  )
}

AdminBillingClientView.propTypes = {
  user: PropTypes.object,
}

export default AdminBillingClientView
